import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import apiService from "../services/apiService";
import "./ManageProperties.css";

const ManageProperties = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState("");
  const [isDeleted, setIsDeleted] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedIsActive, setSelectedIsActive] = useState(""); // Published
  const [selectedIsDiscrete, setSelectedIsDiscrete] = useState(""); // Discrete List
  const [selectedIsFeatured, setSelectedIsFeatured] = useState(""); // Carousel Featured
  const [selectedIsListable, setSelectedIsListable] = useState(""); // Public Listing
  const [filters, setFilters] = useState({
    categories: [],
    locations: [],
    statuses: []
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 0
  });

  useEffect(() => {
    fetchProperties(pagination.currentPage);
  }, []);

  const fetchProperties = async (pageNumber = 1, searchTerm = undefined, deleted = undefined, categoryId = undefined, location = undefined, statusId = undefined, isActive = undefined, isDiscrete = undefined, isFeatured = undefined, isListable = undefined) => {
    try {
      setLoading(true);
      // Use provided values or fall back to state values
      const searchValue = searchTerm !== undefined ? searchTerm : searchCriteria;
      const deletedValue = deleted !== undefined ? deleted : isDeleted;
      const categoryValue = categoryId !== undefined ? categoryId : (selectedCategory ? parseInt(selectedCategory) : null);
      const locationValue = location !== undefined ? location : (selectedLocation || null);
      const statusValue = statusId !== undefined ? statusId : (selectedStatus ? parseInt(selectedStatus) : null);
      
      // Boolean filters: convert empty string to null, "true" to true, "false" to false
      const isActiveValue = isActive !== undefined ? isActive : (selectedIsActive === "" ? null : selectedIsActive === "true");
      const isDiscreteValue = isDiscrete !== undefined ? isDiscrete : (selectedIsDiscrete === "" ? null : selectedIsDiscrete === "true");
      const isFeaturedValue = isFeatured !== undefined ? isFeatured : (selectedIsFeatured === "" ? null : selectedIsFeatured === "true");
      const isListableValue = isListable !== undefined ? isListable : (selectedIsListable === "" ? null : selectedIsListable === "true");
      
      const requestBody = {
        pageNumber: pageNumber,
        searchCriteria: searchValue,
        isDeleted: deletedValue
      };

      // Only add filter values if they're selected
      if (categoryValue) {
        requestBody.categoryId = categoryValue;
      }
      if (locationValue) {
        requestBody.location = locationValue;
      }
      if (statusValue) {
        requestBody.statusId = statusValue;
      }
      if (isActiveValue !== null) {
        requestBody.isActive = isActiveValue;
      }
      if (isDiscreteValue !== null) {
        requestBody.isDiscrete = isDiscreteValue;
      }
      if (isFeaturedValue !== null) {
        requestBody.isFeatured = isFeaturedValue;
      }
      if (isListableValue !== null) {
        requestBody.isListable = isListableValue;
      }

      const data = await apiService.post('/properties/admin', requestBody);
      
      // Handle new response structure with pagination and filters
      if (data.properties && data.pagination) {
        setProperties(data.properties);
        setPagination(data.pagination);
        
        // Update filters if they exist in response
        if (data.filters) {
          setFilters(data.filters);
        }
      } else {
        // Fallback for backward compatibility
        setProperties(Array.isArray(data) ? data : []);
      }
      
      setError(null);
    } catch (err) {
      console.error("Error fetching properties:", err);
      setError("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSearch = () => {
    // Reset to page 1 when filtering
    fetchProperties(1);
  };

  const handleClear = async () => {
    // Clear all state values first
    setSearchCriteria("");
    setIsDeleted(false);
    setSelectedCategory("");
    setSelectedLocation("");
    setSelectedStatus("");
    setSelectedIsActive("");
    setSelectedIsDiscrete("");
    setSelectedIsFeatured("");
    setSelectedIsListable("");
    
    // Directly call API with only the required fields, bypassing state
    try {
      setLoading(true);
      const requestBody = {
        pageNumber: 1,
        searchCriteria: "",
        isDeleted: false
      };

      const data = await apiService.post('/properties/admin', requestBody);
      
      // Handle new response structure with pagination and filters
      if (data.properties && data.pagination) {
        setProperties(data.properties);
        setPagination(data.pagination);
        
        // Update filters if they exist in response
        if (data.filters) {
          setFilters(data.filters);
        }
      } else {
        // Fallback for backward compatibility
        setProperties(Array.isArray(data) ? data : []);
      }
      
      setError(null);
    } catch (err) {
      console.error("Error fetching properties:", err);
      setError("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchProperties(newPage);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "for sale":
        return "for-sale";
      case "for rent":
        return "for-rent";
      case "sold":
        return "sold";
      case "under negotiation":
        return "under-negotiation";
      default:
        return "default";
    }
  };

  const getCategoryColor = (category) => {
    switch (category.toLowerCase()) {
      case "office":
        return "office";
      case "industrial":
        return "industrial";
      case "residential":
        return "residential";
      case "commercial":
        return "commercial";
      case "leisure":
        return "leisure";
      default:
        return "default";
    }
  };

  const handleEditProperty = (propertyId) => {
    navigate(`/properties/${propertyId}`);
  };

  if (loading) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Manage Properties</h1>
        </div>
        <div className="loading-state">
          <p>Loading properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Manage Properties</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchProperties} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1>Manage Properties</h1>
        {pagination.totalCount > 0 && (
          <div className="pagination-info">
            Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} properties
          </div>
        )}
      </div>

      {/* Filter and Search Controls */}
      <div className="filter-controls">
        <div className="filter-left">
          <label htmlFor="keywordSearch">Keyword search:</label>
          <input
            type="text"
            id="keywordSearch"
            className="keyword-input"
            value={searchCriteria}
            onChange={(e) => setSearchCriteria(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleFilterSearch();
              }
            }}
            maxLength={20}
            placeholder="Enter search term"
          />
          <button
            type="button"
            onClick={handleFilterSearch}
            className="filter-search-btn"
          >
            Filter / search
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="clear-btn"
          >
            Clear
          </button>
        </div>
        
        <div className="filter-right">
          <label htmlFor="showArchived" className="checkbox-label">
            <input
              type="checkbox"
              id="showArchived"
              checked={isDeleted}
              onChange={(e) => setIsDeleted(e.target.checked)}
            />
            Show archived properties
          </label>
        </div>
      </div>

      <div className="properties-table">
        <table>
          <thead>
            {/* Filter dropdowns row */}
            <tr className="filter-row">
              <th></th>
              <th></th>
              <th>
                <select
                  id="categoryFilter"
                  className="column-filter-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {filters.categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.title}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  id="locationFilter"
                  className="column-filter-select"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {filters.locations.map((location, index) => (
                    <option key={index} value={location.optionValue}>
                      {location.displayName}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  id="statusFilter"
                  className="column-filter-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {filters.statuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.title}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  id="isActiveFilter"
                  className="column-filter-select"
                  value={selectedIsActive}
                  onChange={(e) => setSelectedIsActive(e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </th>
              <th>
                <select
                  id="isDiscreteFilter"
                  className="column-filter-select"
                  value={selectedIsDiscrete}
                  onChange={(e) => setSelectedIsDiscrete(e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </th>
              <th>
                <select
                  id="isFeaturedFilter"
                  className="column-filter-select"
                  value={selectedIsFeatured}
                  onChange={(e) => setSelectedIsFeatured(e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </th>
              <th>
                <select
                  id="isListableFilter"
                  className="column-filter-select"
                  value={selectedIsListable}
                  onChange={(e) => setSelectedIsListable(e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </th>
            </tr>
            {/* Column headers row */}
            <tr>
              <th>Ref</th>
              <th>Property Name</th>
              <th>Category</th>
              <th>Location</th>
              <th>Status</th>
              <th>Published</th>
              <th>Discrete List</th>
              <th>Carousel Featured</th>
              <th>Public Listing</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((property) => (
              <tr 
                key={property.id}
                className={property.placeholderyn === 1 ? 'special-property-row' : ''}
              >
                <td>
                  <strong>CBRE-{property.id}</strong>
                </td>
                <td>
                  <Link
                    to={`/properties/${property.id}`}
                    className="property-link"
                  >
                    {property.listingTitle}
                    {property.placeholderyn === 1 && <span style={{ color: '#ff6b6b', fontSize: '20px', marginLeft: '8px' }}>*</span>}
                  </Link>
                  <p className="property-description">
                    {property.listingDescription}
                  </p>
                </td>
                <td>
                  {property.category}
                </td>
                <td>
                  <div className="location-info">
                    <div>{property.suburb}</div>
                    <div>{property.province}</div>
                  </div>
                </td>
                <td>
                  {property.propertyStatus}
                </td>
                <td>
                  <span
                    className={`active-badge ${
                      property.isActive ? "active" : "inactive"
                    }`}
                  >
                    {property.isActive ? "Yes" : "No"}
                  </span>
                </td>
                <td>
                  <span
                    className={`discrete-badge ${
                      property.isDiscrete ? "discrete" : "not-discrete"
                    }`}
                  >
                    {property.isDiscrete ? "Yes" : "No"}
                  </span>
                </td>
                <td>
                  <span
                    className={`featured-badge ${
                      property.isFeatured ? "featured" : "not-featured"
                    }`}
                  >
                    {property.isFeatured ? "Yes" : "No"}
                  </span>
                </td>
                <td>
                  <span
                    className={`listable-badge ${
                      property.isListable ? "listable" : "not-listable"
                    }`}
                  >
                    {property.isListable ? "Yes" : "No"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <div className="pagination-pages">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => {
              // Show first page, last page, current page, and pages around current
              const showPage = 
                pageNum === 1 ||
                pageNum === pagination.totalPages ||
                (pageNum >= pagination.currentPage - 2 && pageNum <= pagination.currentPage + 2);
              
              if (!showPage) {
                // Show ellipsis
                if (pageNum === pagination.currentPage - 3 || pageNum === pagination.currentPage + 3) {
                  return <span key={pageNum} className="pagination-ellipsis">...</span>;
                }
                return null;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`pagination-btn ${pageNum === pagination.currentPage ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ManageProperties;
